'use client'

import { useQueries } from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { ChevronDownIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { InlineError } from '@/components/widget/inline-error'
import { OverflowAccountTabs } from '@/components/widget/overflow-account-tabs'
import { OverlayScrollY } from '@/components/widget/overlay-scroll-y'
import { useOrderTypeGuide } from '@/features/dashboard/hooks/use-data-manager-sync'
import { useOrderFormValuesPersist } from '@/features/order/hooks/use-order-form-values-persist'
import { OrderFormEmptyState } from '@/features/order/order-form/components/order-form-empty-state'
import OrderPreviewPanel from '@/features/order/order-form/components/order-preview-panel'
import { OrderPromptBar } from '@/features/order/order-form/components/order-prompt-bar'
import { OrderTypeGuide } from '@/features/order/order-form/components/order-type-guide'
import CollapsibleAreaInOrderForm from '@/features/order/order-form/components/widget/collapsible'
import { SingleHedgeParams } from '@/features/order/order-form/components/widget/single-hedge-params'
import SubmitButton from '@/features/order/order-form/components/widget/submit-button'
import TabFormType from '@/features/order/order-form/components/widget/tab-form-type'
import UnifiedOrderForm from '@/features/order/order-form/components/widget/unified-order-form'
import { useCollapsibleValidation } from '@/features/order/order-form/hooks/use-collapsible-validation'
import { useOrderFormTradeVenue } from '@/features/order/order-form/hooks/use-order-form-trade-venue'
import { useOrderTabLock } from '@/features/order/order-form/hooks/use-order-tab-lock'
import { applyAccountOverrides } from '@/features/order/order-form/lib/apply-account-overrides'
import {
  buildBatchOrderPreviewRequest,
  mapItemsToBatchRowIndex,
} from '@/features/order/order-form/lib/build-batch-order-preview-request'
import { PriceAnchor } from '@/features/order/order-form/lib/build-order-preview-request'
import {
  collectLiquidationPreviews,
  type LiquidationPreview,
} from '@/features/order/order-form/lib/format-order-preview'
import { anyBelowMinNotional } from '@/features/order/order-form/lib/min-notional'
import { orderFormMinNotionalAtomFamily } from '@/features/order/order-form/lib/min-notional-atom'
import {
  OrderFormProvider,
  useUnifiedOrderFormContext,
} from '@/features/order/order-form/lib/order-form-context'
import {
  findLimitPriceDeviations,
  type LimitPriceDeviationRow,
} from '@/features/order/order-form/lib/price-deviation'
import { AnchorPrice, OffsetUnit } from '@/features/order/order-form/lib/resolve-anchor-price'
import { positionsForAccount } from '@/features/order/order-form/lib/single-hedge-tab'
import { resolveOrderSubmitLabel } from '@/features/order/order-form/lib/submit-label'
import { UnifiedItemValues } from '@/features/order/order-form/lib/unified-order-schema'
import {
  buildCreateRfqRequest,
  resolveSingleRfqAccountId,
} from '@/features/rfq/lib/build-rfq-params'
import { interpretCreateRfqResponse } from '@/features/rfq/lib/rfq-create'
import { composeRfqDraft, type OrderFormLegSeed } from '@/features/rfq/lib/rfq-draft'
import { validateRfqDraft } from '@/features/rfq/lib/rfq-validation'
import { useScaleRun } from '@/features/scale/hooks/use-scale-run'
import { useDebounce } from '@/hooks/use-debounce'
import { useNumberFormat } from '@/hooks/use-number-format'
import { usePreferredOrderAccountId } from '@/hooks/use-preferred-order-account'
import { useScreenBreakPoint } from '@/hooks/use-screen'
import { useScrollChaining } from '@/hooks/use-scroll-chaining'
import { getExchangeLabel } from '@/lib/configs/exchange-registry'
import { parseExchangeField, toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { toast } from '@/lib/toast'
import { cn, randomUUID, safeParseFloat } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { useUserAccounts } from '@/service/account.hook'
import { instrumentListQueryOptions, useInstrumentList } from '@/service/instrument.hook'
import { useBatchOrderPreview } from '@/service/order-preview-batch.hook'
import { useCreateRfq } from '@/service/rfq.hook'
import { useConnectedVenueIssues } from '@/service/venue-trading-status.hook'
import { useMultiTickerStream } from '@/service/ws/use-ticker-stream'
import { allPositionsAtom } from '@/state/account'
import {
  orderFormExecutedCountAtomFamily,
  orderFormSelectionAtomFamily,
  orderFormShowAiTextfieldAtomFamily,
  OrderFormSummaryTab,
  orderFormSummaryTabAtomFamily,
  pendingRfqOrdersAtom,
} from '@/state/order'
import { resolveOrderTypeGuideKey } from '@/state/order-type-guide'
import { activeRfqPopupIdsAtom, rfqFormParamsAtomFamily, rfqPopupAccountIdAtom } from '@/state/rfq'
import { Exchange, Instrument, MarketDataType } from '@/types/generated/common/types'
import type {
  BatchOrderPreviewRequest,
  BatchOrderPreviewResponse,
} from '@/types/generated/public_api'

/** Full-bleed banner naming which account the order will hit — thin top/bottom
 * rules only, no fill, so it reads as informational rather than a warning. */
function PlacingOrderBanner({ accountName }: { accountName: string }) {
  return (
    <p className="border-foreground/10 text-foreground w-full border-t border-b px-3 py-1.5 text-center text-xs font-medium">
      {accountName}
    </p>
  )
}

// Below this height a fixed footer leaves too little of the editable ticket visible. This also
// catches the mobile sheet after visualViewport shrinks it above the on-screen keyboard.
const ORDER_FORM_FLOW_FOOTER_HEIGHT = 640

function OrderFormContent({
  compact,
  shortcutOwnerCardId,
  hideCollapsibleArea,
  hideOrderPreview,
}: {
  compact?: boolean
  shortcutOwnerCardId?: string
  hideCollapsibleArea?: boolean
  hideOrderPreview?: boolean
}) {
  const t = useTranslations('orderForm')
  const fmt = useNumberFormat()
  const {
    submitOrder,
    isSubmitting,
    fields,
    form,
    isTwapMode,
    isAdvancedTwapMode,
    isScaleMode,
    isLimitChaseMode,
    isMarketOrder,
    isSingleHedgeMode,
    isSpreaderMode,
    isSingleVisualRow,
    isSplitAccountRow,
    hasMixedSides,
    cardId,
    getSubmissionRows,
    validateSubmissionRows,
    getSingleHedgeTarget,
    hasTriggerCondition,
  } = useUnifiedOrderFormContext()
  const layoutRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const { isMobile } = useScreenBreakPoint()
  const [isShort, setIsShort] = useState(false)
  useEffect(() => {
    const layout = layoutRef.current
    if (!layout) return

    const update = (height: number) => {
      const next = height < ORDER_FORM_FLOW_FOOTER_HEIGHT
      setIsShort((current) => (current === next ? current : next))
    }
    update(layout.getBoundingClientRect().height)

    const observer = new ResizeObserver(([entry]) => update(entry.contentRect.height))
    observer.observe(layout)
    return () => observer.disconnect()
  }, [])
  const footerInScroll = isMobile || isShort
  // The form's own venue (its ticket's), never another widget's focused tab.
  const cardTradeVenue = useOrderFormTradeVenue(cardId)
  // The last account explicitly picked in the Order Form for this venue, else the venue
  // default — no card tier (the linking group's tab account belongs to the chart). Used
  // below only as the fallback for a row that hasn't materialized its own accountId yet
  // (preview grouping), never for actual submission — getSubmissionRows/
  // resolveItemAccountId own that.
  const cardTradingAccountId = usePreferredOrderAccountId(cardTradeVenue)
  const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  const { data: instrumentListData } = useInstrumentList(primaryExchange)
  // Every account the user has, each with its own real tradeVenue — used
  // below to resolve accountId -> exchange for the batch-preview group
  // lookup (see accountIdToExchange), same source order-preview-panel.tsx
  // already uses for its own account-override picker.
  const { data: userAccountsData } = useUserAccounts()
  const cardAccount = userAccountsData?.accounts.find(
    (account) => account.accountId === cardTradingAccountId
  )
  const selectedTab = useAtomValue(orderFormSummaryTabAtomFamily(cardId))
  const guideKey = resolveOrderTypeGuideKey({
    tab: selectedTab,
    isSpreaderMode,
    hasTriggerCondition,
  })
  // Spreader mode ignores the order-type tab (and gates itself where it's entered).
  const tabLock = useOrderTabLock(selectedTab)
  const planLock = isSpreaderMode ? null : tabLock
  const { isSeen: isGuideSeen, markSeen: markGuideSeen } = useOrderTypeGuide()

  useOrderFormValuesPersist(cardId, form)

  // Hands the wheel to the page once the form hits its top/bottom (the hook's default), so
  // scrolling carries on through the dashboard instead of stopping dead at the form's edge.
  useScrollChaining(footerInScroll ? layoutRef : scrollRef)

  // Visual-row-based, not raw fields.length — a ticker split across 2+
  // accounts is still one row, not "multi-leg" (see row-group-key.ts /
  // use-unified-order-form.ts's isSingleVisualRow).
  const isMultiLeg = !isSingleVisualRow

  // Determine the primary side for single-leg orders (from the first field).
  // Only meaningful when the single visual row is NOT mixed-sided — a row
  // split Long on one account / Short on another has no single "the side"
  // (submission itself is unaffected: each item's own `.side` is what
  // actually gets sent, see getSubmissionRows/submitLimitMarketOrder — this
  // value is informational/UI-only, e.g. the submit button's label/color).
  // Falls back to 'buy' as a neutral placeholder when mixed, rather than
  // silently reporting whichever item happens to be first.
  // Watch the form state to ensure it reacts to side changes.
  const primarySide = hasMixedSides
    ? 'buy'
    : form.watch('items.0.side') || (fields.length > 0 ? fields[0].side : 'buy')

  // Watch form values for validation
  const items = form.watch('items')
  const twapIntervalMinutes = form.watch('twapIntervalMinutes')
  const twapFrequencyMode = form.watch('twapFrequencyMode')
  const twapOrderSizeMode = form.watch('twapOrderSizeMode')
  const hasOrderRows = fields.length > 0 && (items?.length ?? 0) > 0

  // Core amount validation: All items must have an amount > 0
  const hasAmount = items?.every((item) => item.amount && safeParseFloat(item.amount) > 0)

  // TWAP validity mirrors buildTwapAlgoParams: send EITHER a duration (backend
  // computes cadence) OR an explicit cadence (frequency + order size). The two
  // are mutually exclusive, so a duration-only TWAP must be submittable.
  const twapDurationOk = safeParseFloat(twapIntervalMinutes) > 0
  const twapFrequencyOk =
    twapFrequencyMode === 'random'
      ? safeParseFloat(form.watch('twapFrequencyMinSeconds')) > 0 &&
        safeParseFloat(form.watch('twapFrequencyMaxSeconds')) > 0
      : safeParseFloat(form.watch('twapFrequencySeconds')) > 0
  const twapOrderSizeOk =
    twapOrderSizeMode === 'random'
      ? safeParseFloat(form.watch('twapOrderSizeMin')) > 0 &&
        safeParseFloat(form.watch('twapOrderSizeMax')) > 0
      : safeParseFloat(form.watch('twapOrderSize')) > 0
  const isTwapValid =
    !isTwapMode || (twapDurationOk && !twapOrderSizeOk) || (twapOrderSizeOk && twapFrequencyOk)

  // Advanced TWAP validity: duration required, band percent required when the band
  // toggle is on, and — since the contract is "single instrument, single account" — the
  // row must not be a multi-leg basket or a single-instrument row fanned across multiple
  // accounts (submitAdvancedTwapOrder's own rows.length > 1 check is the defensive
  // second guard for this). No catchup checks here — the catchup leg is derived
  // automatically at submit time, not user input (see derive-catchup-params.ts).
  const advancedTwapDurationSeconds = form.watch('advancedTwapDurationSeconds')
  const advancedTwapBandEnabled = form.watch('advancedTwapBandEnabled')
  const advancedTwapBandPct = form.watch('advancedTwapBandPct')
  const advancedTwapDurationOk = safeParseFloat(advancedTwapDurationSeconds) > 0
  const advancedTwapBandOk = !advancedTwapBandEnabled || safeParseFloat(advancedTwapBandPct) > 0
  const advancedTwapSingleAccountOk = isSingleVisualRow && !isSplitAccountRow
  const isAdvancedTwapValid =
    !isAdvancedTwapMode ||
    (advancedTwapDurationOk && advancedTwapBandOk && advancedTwapSingleAccountOk)

  // Scale validity: one account (single run per submit) and a ladder the planner accepts —
  // the same plan ScaleParams previews and submitScaleOrder sends, so the button, the
  // preview and the request never disagree.
  const { run: scaleRun } = useScaleRun(isScaleMode)
  const isScaleValid =
    !isScaleMode || (isSingleVisualRow && !isSplitAccountRow && scaleRun?.plan?.ok === true)

  const isAboveMinTradeAmount = useMemo(() => {
    const optionFields = fields.filter((f) => f.itemType === 'option')
    if (optionFields.length === 0) return true
    return optionFields.every((field) => {
      const instData = instrumentListData?.instruments.find((i) => i.symbol === field.symbol)
      const minAmt = safeParseFloat(instData?.minTradeAmount ?? '0')
      if (minAmt <= 0) return true
      const fieldIndex = fields.findIndex((f) => f.symbol === field.symbol)
      const amt = safeParseFloat(items?.[fieldIndex]?.amount ?? '0')
      return amt <= 0 || amt >= minAmt
    })
  }, [fields, instrumentListData, items])

  // Trigger / Exit completeness: blocks submit and surfaces the specific reason
  // (e.g. "Timeframe is required") above the button. Only the active sections
  // contribute — a disabled trigger or exit never blocks.
  const symbols = useMemo(
    () => (items?.map((item) => item.symbol).filter(Boolean) as string[]) ?? [],
    [items]
  )
  const collapsibleError = useCollapsibleValidation({
    cardId,
    symbols,
    side: primarySide as 'buy' | 'sell',
    isSingleVisualRow,
    hasMixedSides,
    isTwapMode,
    isAdvancedTwapMode,
    isScaleMode,
    isLimitChaseMode,
    isSingleHedgeMode,
    isSpreaderMode,
  })

  // A hedge reads no row sizes: it only needs an account with something open to hedge (else
  // SingleHedgeParams shows its empty state). Its own field errors show inline there and are
  // toasted on submit. Selected as a boolean so position ticks don't re-render the form.
  const singleHedgeTarget = isSingleHedgeMode ? getSingleHedgeTarget() : null
  const hedgeAccountId = singleHedgeTarget?.account.accountId
  const hedgeAccountHasPositions = useAtomValue(
    useMemo(
      () =>
        selectAtom(
          allPositionsAtom,
          (positions) =>
            !hedgeAccountId || positionsForAccount(positions, hedgeAccountId).length > 0
        ),
      [hedgeAccountId]
    )
  )
  const isFormValid = isSingleHedgeMode
    ? !collapsibleError && singleHedgeTarget !== null && hedgeAccountHasPositions
    : Boolean(
        hasOrderRows &&
        hasAmount &&
        isTwapValid &&
        isAdvancedTwapValid &&
        isScaleValid &&
        !collapsibleError
      ) // && isAboveMinTradeAmount)

  // State-aware button text: "Enter size" until every row has an amount, then "Buy/Sell
  // <symbol>" for a single-instrument, single-sided ticket. Multi-leg and mixed-side tickets
  // keep the generic "Submit Order" — there is no one side or symbol to name.
  const submitLabelState = resolveOrderSubmitLabel({
    hasOrderRows,
    hasAmount: Boolean(hasAmount),
    isSingleSided: isSingleVisualRow && !hasMixedSides,
    side: primarySide === 'sell' ? 'sell' : 'buy',
    symbol: formatSymbol(symbols[0]),
  })
  const stateSubmitLabel =
    submitLabelState?.kind === 'enterSize'
      ? t('enterSize')
      : submitLabelState?.kind === 'side'
        ? t(submitLabelState.side === 'buy' ? 'buySymbol' : 'sellSymbol', {
            symbol: submitLabelState.symbol,
          })
        : undefined

  // ── RFQ mode ────────────────────────────────────────────────────────────────
  // RFQ runs as an order-form mode (like TWAP): the legs come from the order rows
  // above, the RFQ-only params (makers / auto / hedge) from the per-card atom. The
  // submit builds a CreateRfqRequest instead of an order.
  const isRfqMode = selectedTab === OrderFormSummaryTab.RFQ
  const rfqParams = useAtomValue(rfqFormParamsAtomFamily(cardId))

  // While a manually-submitted RFQ is still open, GlobalRfqPopups (mounted once in
  // DashboardLayout, keyed by rfqId — not this card) shows the quote-accept popup. This
  // component only needs to register the id on submit; all lifecycle/cleanup effects
  // live there now, account-wide, so they keep running even if this card's widget (or
  // the order-form-sheet it's embedded in) closes.
  const setActiveRfqPopupIds = useSetAtom(activeRfqPopupIdsAtom)
  const setRfqPopupAccountId = useSetAtom(rfqPopupAccountIdAtom)
  const setPendingRfqOrders = useSetAtom(pendingRfqOrdersAtom)
  const bumpExecutedCount = useSetAtom(orderFormExecutedCountAtomFamily(cardId))

  // Computed inline, NOT memoized on `items`: react-hook-form mutates the items array in
  // place on setValue, so its reference is stable across amount edits — a useMemo keyed on
  // `items` goes stale and only refreshes when something else changes (e.g. toggling
  // manual/auto). The component re-renders on every form change via form.watch, so an
  // inline recompute always reflects the latest sizes.
  const rfqDraft = composeRfqDraft((items ?? []) as OrderFormLegSeed[], rfqParams)
  // Gate the submit on the concrete, user-evident condition — at least one leg, each with
  // a positive size — rather than the full RFQ rule set. Entering a size therefore always
  // re-enables the button; deeper venue rules (e.g. the buy-leg requirement) are checked
  // on submit and surfaced as a toast instead of a sticky inline warning.
  const rfqLegs = (items ?? []).filter((i) => i?.symbol)
  const isRfqSubmittable =
    rfqLegs.length > 0 && rfqLegs.every((l) => safeParseFloat(l.amount ?? '') > 0)

  const { mutate: createRfq, isPending: rfqPending } = useCreateRfq({
    onSuccess: (data, variables) => {
      bumpExecutedCount((n) => n + 1)
      const handle = interpretCreateRfqResponse(data)
      if (handle.mode === 'auto') toast.success(t('autoRfqStarted'), { position: 'bottom-right' })
      else if (handle.mode === 'manual') {
        toast.success(t('rfqSubmitted'), { position: 'bottom-right' })
        setActiveRfqPopupIds((prev) =>
          prev.includes(handle.rfqId) ? prev : [...prev, handle.rfqId]
        )
        // Snapshot the account that actually submitted this RFQ from the mutation's
        // own variables — NOT re-derived from current card/tab state, which may have
        // changed by the time this response lands (see rfqPopupAccountIdAtom's doc).
        setRfqPopupAccountId((prev) => ({ ...prev, [handle.rfqId]: variables.accountId }))
        // Stamp the resolved rfqId onto the pending submission so a later async
        // rejection notification (see notification-manager.tsx) can find and drop
        // this popup by id; cleared once the stream confirms the RFQ is genuinely
        // live (see RfqPopupItem).
        const submissionId = variables.correlationId
        if (submissionId) {
          setPendingRfqOrders((prev) =>
            prev[submissionId]
              ? { ...prev, [submissionId]: { ...prev[submissionId], rfqId: handle.rfqId } }
              : prev
          )
        }
      } else toast.success(t('rfqDispatched'), { position: 'bottom-right' })
    },
    onError: (error, variables) => {
      const submissionId = variables.correlationId
      if (submissionId) {
        setPendingRfqOrders((prev) => {
          if (!prev[submissionId]) return prev
          const next = { ...prev }
          delete next[submissionId]
          return next
        })
      }
      toast.error(error.message || t('failedSubmitRfq'), { position: 'bottom-right' })
    },
  })

  const accountId = cardTradingAccountId ?? ''

  // Per-account preview override: which account's numbers a given real
  // account's OrderPreviewPanel should show instead of its own. Keyed by the
  // real account_id (one of `accountIds` below); `undefined` for a key means
  // "no override, show the real account's own preview". Lives here (not in
  // the panel) so it can be threaded into the actual batch request below —
  // the panel only reads/controls it via props.
  const [accountOverrides, setAccountOverrides] = useState<Record<string, string | undefined>>({})

  // Which real account's preview panel to show when the ticket spans
  // multiple accounts — a horizontally-scrolling row of full panels made
  // every account but the first-in-view unreadable in the form's narrow
  // width, so multi-account tickets instead page through one panel at a
  // time via a tab strip (see the tab-driven render further down).
  // `undefined`/no-longer-valid falls back to the first real account rather
  // than needing a reset effect.
  const [selectedPreviewAccountId, setSelectedPreviewAccountId] = useState<string>()
  // Preview stat grid open/closed. Session-only, defaults open; shared across the account
  // tabs so switching tabs doesn't reopen what the user collapsed.
  const [previewExpanded, setPreviewExpanded] = useState(true)

  // Group rows by their real submitting account — a row missing its own
  // accountId (not all creation paths populate it) falls back to this
  // card's single form-level account, matching pre-batch single-row behavior.
  // Computed ahead of the batch-request memo below so that memo (and the
  // override-pruning effect further down) can gate on `accountIds.length`.
  //
  // react-hook-form mutates `items` (and each item object) in place on
  // setValue — same hazard itemsPreviewSnapshot below solves — so memoizing
  // directly on `items`' own reference can miss an in-place accountId change
  // (e.g. re-picking a row's instrument to resolve to a different account)
  // and leave accountIds/isSingleAccountTicket stale, which in turn means the
  // pruning effect further down (keyed only on accountIds) never re-fires.
  // accountIds must stay synchronous (it gates isSingleAccountTicket, read
  // immediately below and elsewhere in this render), so — unlike
  // itemsPreviewSnapshot — this is NOT debounced: it's a fresh, cheap string
  // every render, and the useMemo below only recomputes its VALUE (and
  // therefore only produces a new array reference) when that string actually
  // changes, so the pruning effect still only fires on a real account-set
  // change, not every render.
  const itemAccountIdsSnapshot = JSON.stringify((items ?? []).map((item) => item?.accountId))
  const accountIds = useMemo(
    () =>
      Array.from(
        new Set(
          (items ?? []).map(
            (item) => (item as UnifiedItemValues | undefined)?.accountId ?? accountId
          )
        )
      ).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemAccountIdsSnapshot, accountId]
  )
  const isSingleAccountTicket = accountIds.length === 1

  // accountId -> resolved exchange, so the batch-preview group lookup
  // further down can match on (exchange, accountId) together, not accountId
  // alone — the backend deliberately groups by that pair (see
  // AccountGroupPreview's own doc comment) because account_id isn't
  // guaranteed globally unique across exchanges; matching on accountId alone
  // risks silently rendering a different exchange's numbers. Sourced from the
  // user's own account list (each account's tradeVenue), not `item.exchange`
  // — OrderPreviewRequest carries no exchange field at all, so the backend
  // always derives a row's exchange from its accountId's real trade_venue
  // server-side, including for an overridden preview account (see
  // effectiveAccId below) that a row's own item.exchange knows nothing
  // about. Recomputed fresh every render rather than memoized — nothing
  // downstream depends on this Map's reference identity, only its contents.
  const accountIdToExchange = new Map<string, Exchange>()
  ;(userAccountsData?.accounts ?? []).forEach((account) => {
    accountIdToExchange.set(
      account.accountId,
      toExchangeEnum(tradeVenueToExchangeKey(account.tradeVenue))
    )
  })

  // A venue this ticket's rows go to that can't take orders right now (the connection
  // status's signal: routing off, trading probe failing, outage, maintenance). Said on the
  // submit button itself — during an outage no fill or rejection ever comes back, so the
  // button is the one place the user is sure to see it before (re)sending.
  const venueIssues = useConnectedVenueIssues()
  const rowVenueIssue = venueIssues.find((issue) =>
    accountIds.some((accountId) => accountIdToExchange.get(accountId) === issue.exchange)
  )
  const venueIssueLabel = rowVenueIssue
    ? t(rowVenueIssue.kind === 'maintenance' ? 'venueMaintenanceButton' : 'venueOfflineButton', {
        venue: getExchangeLabel(rowVenueIssue.exchange),
      })
    : undefined
  // Certain to be rejected (routing switched off server-side): don't let it send at all.
  const venueBlocksSubmit = rowVenueIssue?.blocksOrders ?? false

  // The override control (see allowAccountOverride below) is only ever shown
  // — and therefore only ever clearable — while the ticket is single-account.
  // Whenever the set of real accounts changes, drop any override that no
  // longer belongs to the ticket's current sole account so a stale override
  // never silently reappears if the user later returns to that same account
  // id. Mirrors the pre-batch per-panel `useEffect(() => setPreviewedAccountId
  // (null), [accountId])` reset from before this state moved up here.
  useEffect(() => {
    setAccountOverrides((prev) => {
      const soleId = isSingleAccountTicket ? accountIds[0] : undefined
      const kept = soleId ? prev[soleId] : undefined
      const next = kept ? { [soleId as string]: kept } : {}
      const prevKeys = Object.keys(prev)
      const nextKeys = Object.keys(next)
      const unchanged =
        prevKeys.length === nextKeys.length && nextKeys.every((k) => prev[k] === next[k])
      return unchanged ? prev : next
    })
  }, [accountIds, isSingleAccountTicket])

  // react-hook-form mutates each row's item object (and the `items` array
  // itself) in place on setValue — same hazard as rfqDraft above — so
  // debouncing/memoizing directly on `items`' own reference would freeze the
  // batch request at whatever values existed the last time the array was
  // rebuilt (a row added/removed), never picking up a plain amount/symbol
  // keystroke. Debounce a serialized snapshot of only the fields that feed
  // the preview request instead; that snapshot is a fresh string every
  // render, so ordinary `!==` string comparison inside useDebounce correctly
  // detects an edit, and the memo below always reads the live `items` once
  // the snapshot settles.
  const itemsPreviewSnapshot = JSON.stringify(
    (items ?? []).map((item) => ({
      symbol: item?.symbol,
      side: item?.side,
      amount: item?.amount,
      amountUnit: item?.amountUnit,
      itemType: item?.itemType,
      baseCurrency: item?.baseCurrency,
      accountId: item?.accountId,
      exchange: item?.exchange,
    }))
  )
  const { value: debouncedItemsSnapshot } = useDebounce(itemsPreviewSnapshot, 250)
  const formPrice = form.watch('price') ?? ''
  const { value: debouncedPrice } = useDebounce(formPrice, 250)
  const priceAnchor = (form.watch('priceAnchor') ?? 'price') as PriceAnchor
  const priceOffsetUnit = (form.watch('priceOffsetUnit') ?? 'quote_value') as OffsetUnit

  // Rows can span exchanges (multi-account batch preview) — resolve
  // denomination/contract-size against EACH ROW'S OWN exchange's instrument
  // list, not `instrumentListData` above (scoped to the active account's
  // single primaryExchange), or a row on a different exchange than the
  // active account gets the wrong instrument's data entirely. Same fix
  // unified-order-form.tsx already applies via fieldExchanges/
  // getFieldInstrument. `useQueries` (not multiple useInstrumentList calls)
  // because the set of distinct exchanges across `items` is dynamic. Keyed
  // on `debouncedItemsSnapshot` (which includes each item's own `exchange`),
  // not `items`' own reference — same in-place-mutation hazard as accountIds
  // above, and this way it settles on the same tick as `batchRequest` below,
  // which depends on the same snapshot.
  const itemExchanges = useMemo(
    () => [
      ...new Set((items ?? []).map((item) => parseExchangeField(item?.exchange, primaryExchange))),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedItemsSnapshot, primaryExchange]
  )
  const itemExchangeInstrumentQueries = useQueries({
    queries: itemExchanges.map((ex) => instrumentListQueryOptions(ex)),
  })
  const instrumentsByExchange = useMemo(() => {
    const map = new Map<Exchange, Instrument[]>()
    itemExchangeInstrumentQueries.forEach((q, i) => {
      map.set(itemExchanges[i], q.data?.instruments ?? [])
    })
    return map
  }, [itemExchangeInstrumentQueries, itemExchanges])

  // Live per-symbol best bid/ask, used below to resolve a relative price
  // anchor (mid/best_bid/best_ask) into the actual absolute price the batch
  // preview request should use for THAT row — same math the "Estimated
  // price" hint under the price field shows the user (see
  // buildOrderPreviewRequest's doc comment).
  //
  // This subscribes directly via useMultiTickerStream rather than reading
  // watchListTickerMapAtom (the watchlist's shared cache) — that atom is
  // only ever populated by whichever widget explicitly subscribes to a
  // symbol (see watchlist-view.tsx's own visibleInstruments-only
  // subscription), so a leg's symbol has no ticker there at all unless some
  // OTHER open widget happens to already be watching it. Same fix
  // twap-order-card-v1.tsx applies for its own order symbols, with the same
  // reasoning in its comment ("watchlist only subscribes to visible items").
  const legTickerPayloads = useMemo(() => {
    const seen = new Set<string>()
    const payloads: { symbol: string; exchange: Exchange; dataType: MarketDataType }[] = []
    ;(items ?? []).forEach((item) => {
      if (!item?.symbol) return
      const exchange = parseExchangeField(item.exchange, primaryExchange)
      const key = `${exchange}:${item.symbol}`
      if (seen.has(key)) return
      seen.add(key)
      payloads.push({
        symbol: item.symbol,
        exchange,
        dataType: MarketDataType.MARKET_DATA_TYPE_TICKER,
      })
    })
    return payloads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedItemsSnapshot, primaryExchange])
  const [tickerBySymbol, setTickerBySymbol] = useState<Record<string, AnchorPrice | undefined>>({})
  useMultiTickerStream({
    connected: legTickerPayloads.length > 0,
    payloads: legTickerPayloads,
    onMessage: (data) => {
      if (!data?.tickers?.length) return
      setTickerBySymbol((prev) => {
        let changed = false
        const next = { ...prev }
        data.tickers.forEach((ticker) => {
          if (!ticker.symbol) return
          const existing = next[ticker.symbol]
          if (
            existing?.bestBidPrice === ticker.bidPrice &&
            existing?.bestAskPrice === ticker.askPrice
          )
            return
          next[ticker.symbol] = {
            bestBidPrice: ticker.bidPrice,
            bestAskPrice: ticker.askPrice,
          }
          changed = true
        })
        return changed ? next : prev
      })
    },
  })

  // Each row's own tick size, resolved against ITS OWN exchange's instrument
  // list — same per-row-exchange fix instrumentsByExchange applies above —
  // so a relative price rounds to the wrong grid if it borrowed another
  // leg's tick size.
  const tickSizeBySymbol = useMemo(() => {
    const map: Record<string, number | undefined> = {}
    ;(items ?? []).forEach((item) => {
      if (!item?.symbol || map[item.symbol] !== undefined) return
      const instruments =
        instrumentsByExchange.get(parseExchangeField(item.exchange, primaryExchange)) ?? []
      const inst = instruments.find((i) => i.symbol === item.symbol)
      const tickSize = Number(inst?.tickSize)
      map[item.symbol] = Number.isFinite(tickSize) && tickSize > 0 ? tickSize : undefined
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedItemsSnapshot, instrumentsByExchange, primaryExchange])

  // Opens below the venue's minimum notional block submit. The order rows compute the per-row
  // result once (price, unit and instrument resolved there) and the row message reads the same entry.
  const minNotionalByIndex = useAtomValue(orderFormMinNotionalAtomFamily(cardId))
  const minNotionalBlocked = !isSingleHedgeMode && anyBelowMinNotional(minNotionalByIndex)

  // Fat-finger guard: a limit that crosses the book by more than 1% (buy over the best ask, sell
  // under the best bid) shows a warning above the submit button and asks for confirmation before
  // it sends. Uses the live price, not the debounced one, so the confirm can't miss a fresh edit.
  const isLimitPriceTicket =
    !isMarketOrder &&
    !isLimitChaseMode &&
    !isTwapMode &&
    !isAdvancedTwapMode &&
    !isRfqMode &&
    !isSpreaderMode &&
    !isSingleHedgeMode
  const priceDeviations = useMemo(
    () =>
      isLimitPriceTicket
        ? findLimitPriceDeviations({
            items: items ?? [],
            formPrice,
            priceAnchor,
            priceOffsetUnit,
            tickerBySymbol,
            tickSizeBySymbol,
          })
        : [],
    [
      isLimitPriceTicket,
      items,
      formPrice,
      priceAnchor,
      priceOffsetUnit,
      tickerBySymbol,
      tickSizeBySymbol,
    ]
  )
  const [deviationConfirmOpen, setDeviationConfirmOpen] = useState(false)
  const priceDeviationMessage = ({ symbol, deviation }: LimitPriceDeviationRow) =>
    `${isMultiLeg ? `${formatSymbol(symbol)}: ` : ''}${t(
      deviation.reference === 'best_ask' ? 'priceDeviationAboveAsk' : 'priceDeviationBelowBid',
      {
        pct: fmt.number(deviation.deviation * 100, { maximumFractionDigits: 2 }),
        price: fmt.number(deviation.referencePrice, { maximumSignificantDigits: 8 }),
      }
    )}`

  // Built together so they always describe the same underlying snapshot —
  // batchRowIndexByItem maps items[i] to its row index in batchRequest's
  // orders (and therefore in the response's `rows`), which diverges from i
  // itself as soon as an earlier row is incomplete (see
  // mapItemsToBatchRowIndex's own doc comment).
  const { batchRequest, batchRowIndexByItem } = useMemo(() => {
    // Remap each row's accountId through any active preview override BEFORE
    // building the request, so a user previewing under a different account
    // actually gets that account's server-computed numbers back, not the
    // real submitting account's. Only ever applies for a single-account
    // ticket (see applyAccountOverrides' own doc comment) — otherwise a
    // stale override left over from an earlier single-account state would
    // keep silently remapping rows with no visible control left to clear it.
    const effectiveItems = applyAccountOverrides(
      (items ?? []) as UnifiedItemValues[],
      accountOverrides,
      accountId,
      isSingleAccountTicket
    )
    return {
      // Nothing renders the batch preview while in RFQ mode (see !isRfqMode
      // gating OrderPreviewPanel's render further down) — gate the request
      // itself the same way, or it keeps firing on every debounced edit for
      // a result nothing ever displays.
      batchRequest:
        isRfqMode || isSingleHedgeMode
          ? null
          : buildBatchOrderPreviewRequest({
              items: effectiveItems,
              formPrice: debouncedPrice,
              priceAnchor,
              priceOffsetUnit,
              isMarketOrder,
              isLimitChaseMode,
              instrumentsByExchange,
              primaryExchange,
              tickerBySymbol,
              tickSizeBySymbol,
            }),
      batchRowIndexByItem: mapItemsToBatchRowIndex(effectiveItems),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedItemsSnapshot,
    debouncedPrice,
    priceAnchor,
    priceOffsetUnit,
    isMarketOrder,
    isLimitChaseMode,
    instrumentsByExchange,
    primaryExchange,
    tickerBySymbol,
    tickSizeBySymbol,
    accountOverrides,
    accountId,
    isSingleAccountTicket,
    isRfqMode,
    isSingleHedgeMode,
  ])
  // `useBatchOrderPreview` uses `placeholderData: keepPreviousData`, which
  // serves the LAST successful response indefinitely once `batchRequest`
  // goes `null` (every row cleared/invalid, or RFQ mode) — regardless of the
  // query's `enabled` state. Blank it explicitly here rather than let a
  // stale preview linger with nothing live backing it; the old single-row
  // order-preview-panel.tsx guarded against this exact hazard the same way.
  const { data: rawBatchPreview, isPlaceholderData } = useBatchOrderPreview(batchRequest)
  const batchPreview = batchRequest ? rawBatchPreview : undefined
  // Liquidation prices are per row, so they're read by zipping `rows` with the request's
  // `orders`. A placeholder response (keepPreviousData, while the next request is in flight)
  // belongs to the PREVIOUS request, whose rows may not line up with the current one — so the
  // zip uses the last request/response pair that actually settled together.
  const [settledPreviewPair, setSettledPreviewPair] = useState<{
    orders: BatchOrderPreviewRequest['orders']
    rows: BatchOrderPreviewResponse['rows']
  } | null>(null)
  useEffect(() => {
    if (isPlaceholderData || !batchRequest || !rawBatchPreview) return
    // Keyed on the response's `rows` identity: `batchRequest` is rebuilt whenever any memo
    // dep changes identity, and storing a fresh pair each time re-rendered this component
    // into a "Maximum update depth exceeded" loop. A settled response with the same rows
    // answered an equivalent request, so the previous pair still lines up.
    setSettledPreviewPair((prev) =>
      prev?.rows === rawBatchPreview.rows
        ? prev
        : { orders: batchRequest.orders, rows: rawBatchPreview.rows }
    )
  }, [isPlaceholderData, batchRequest, rawBatchPreview])
  // One stable array per account per settled pair, so the memoized panel isn't re-rendered
  // by a fresh-but-equal prop on every keystroke.
  const hasBatchRequest = batchRequest !== null
  const liquidationPreviewsFor = useMemo(() => {
    const pair = hasBatchRequest ? settledPreviewPair : null
    const cache = new Map<string, LiquidationPreview[]>()
    return (accId: string) => {
      let previews = cache.get(accId)
      if (!previews) {
        previews = collectLiquidationPreviews(pair?.orders, pair?.rows, accId)
        cache.set(accId, previews)
      }
      return previews
    }
  }, [hasBatchRequest, settledPreviewPair])
  // Built once per batchPreview rather than re-scanning `.groups` with
  // `.find()` inside the accountIds.map() below — that pattern is
  // O(accounts × groups), up to 400 comparisons at the 20-row/account cap.
  const groupsByAccountAndExchange = useMemo(() => {
    const map = new Map<string, NonNullable<typeof batchPreview>['groups'][number]>()
    batchPreview?.groups.forEach((g) => map.set(`${g.accountId}:${g.exchange}`, g))
    return map
  }, [batchPreview])
  // Stable per-account callback identity — OrderPreviewPanel is wrapped in
  // React.memo, which is defeated if it receives a freshly-created closure
  // on every render (as an inline `(newId) => ...` arrow function would).
  // Keyed by accId and only rebuilt when the set of real accounts changes,
  // not on every keystroke elsewhere in the form.
  const previewedAccountChangeHandlers = useMemo(() => {
    const handlers = new Map<string, (newId: string) => void>()
    accountIds.forEach((accId) => {
      handlers.set(accId, (newId: string) =>
        setAccountOverrides((prev) => ({
          ...prev,
          [accId]: newId === accId ? undefined : newId,
        }))
      )
    })
    return handlers
  }, [accountIds])

  const handleRfqSubmit = () => {
    if (!validateSubmissionRows()) return

    const submissionRows = getSubmissionRows()
    if (submissionRows.length === 0) {
      toast.error(t('noAccountSelected'), { position: 'bottom-right' })
      return
    }
    const rfqAccountId = resolveSingleRfqAccountId(
      submissionRows.map(({ account }) => account.accountId)
    )
    if (!rfqAccountId) {
      toast.error(t('rfqSingleAccountOnly'), { position: 'bottom-right' })
      return
    }
    const errors = validateRfqDraft(rfqDraft, t)
    if (errors.length > 0) {
      toast.error(errors[0].message, { position: 'bottom-right' })
      return
    }
    const request = buildCreateRfqRequest(rfqDraft, rfqAccountId)
    // Stamp every RFQ so notification errors can be matched while the quote view is pending.
    const submissionId = randomUUID()
    request.correlationId = submissionId
    if (rfqDraft.executionMode === 'manual') {
      setPendingRfqOrders((prev) => ({
        ...prev,
        [submissionId]: { submissionId, cardId, submittedAt: Date.now() },
      }))
    }
    createRfq(request)
  }

  // UnifiedOrderForm swaps its whole body for a "select a symbol" prompt at
  // exactly this condition. The footer below (preview panel, account bar,
  // submit button) has to disappear with it — otherwise the card reads as a
  // ready-to-send ticket for an account, with a Submit button, while the form
  // above is telling the user they haven't picked anything yet.
  // The Hedge tab needs no rows, so it is never an empty ticket.
  const isEmptyTicket = fields.length === 0 && !isSingleHedgeMode

  const rfqSubmitLabel = rfqPending
    ? t('submitting')
    : rfqDraft.executionMode === 'auto'
      ? t('startAutoRfq')
      : t('requestQuotes')

  return (
    <OverlayScrollY
      scrollerRef={layoutRef}
      className="h-full w-full"
      scrollerClassName={cn(
        'relative flex h-full w-full flex-col overscroll-y-contain',
        footerInScroll ? 'overflow-x-hidden overflow-y-auto' : 'overflow-hidden'
      )}
    >
      <OverlayScrollY
        scrollerRef={scrollRef}
        className={footerInScroll ? 'shrink-0' : 'flex-1'}
        scrollerClassName={cn(
          'flex flex-col',
          footerInScroll ? 'overflow-visible' : 'h-full overflow-x-hidden overscroll-y-contain'
        )}
      >
        <TabFormType />
        {isSingleHedgeMode ? (
          <SingleHedgeParams />
        ) : (
          <UnifiedOrderForm
            batchPreview={batchPreview}
            batchRowIndexByItem={batchRowIndexByItem}
            priceWarnings={
              priceDeviations.length > 0 && (
                <div className="mx-1 flex flex-col gap-1">
                  {priceDeviations.map((row) => (
                    <InlineError key={row.key}>{priceDeviationMessage(row)}</InlineError>
                  ))}
                </div>
              )
            }
          />
        )}
        {/* Nothing to trigger before an instrument is picked (a single hedge needs no row). */}
        {!isRfqMode && !hideCollapsibleArea && (hasOrderRows || isSingleHedgeMode) && (
          <CollapsibleAreaInOrderForm />
        )}
        {/* RFQ builds its own legs and a single hedge needs no row, so neither waits for one. */}
        {guideKey && (hasOrderRows || isRfqMode || isSingleHedgeMode) && !isGuideSeen(guideKey) && (
          <OrderTypeGuide guideKey={guideKey} onClose={() => markGuideSeen(guideKey)} />
        )}
      </OverlayScrollY>
      {/* Preview panel, banner, and submit button all live outside the scroll
          container, in flow after it — the scroll area above takes the remaining
          height (flex-1 min-h-0) and this footer takes its own natural height, so
          the three stay anchored together at the bottom instead of scrolling away.
          The banner stays immediately above the submit button; the preview panel
          (when present) sits above the banner, not between it and the button. */}
      {/* Unmounted rather than hidden: SubmitButton registers a submit
          keyboard shortcut while mounted, and an empty ticket should own no
          such shortcut at all. */}
      {isEmptyTicket ? null : (
        // mt-auto pins the footer to the bottom when the form above is shorter than the
        // widget (matters in footerInScroll, where the form doesn't flex-1 fill).
        // In footerInScroll the preview panel and banner scroll with the form (a short widget
        // can't spare their height), but the submit button stays reachable: the wrapper turns
        // `contents`, so the submit block is a child of the scroll container itself — a sticky
        // element only travels within its parent, and this wrapper sits at the very end of the
        // scroll — and pins to its bottom. The zero-height spacer keeps the mt-auto pinning.
        <div
          className={footerInScroll ? 'contents' : 'mt-auto flex w-full shrink-0 flex-col gap-0'}
        >
          {footerInScroll && <div aria-hidden className="mt-auto" />}
          {!hideOrderPreview &&
            !isRfqMode &&
            !isSingleHedgeMode &&
            (() => {
              const panels = accountIds.map((accId) => {
                // The effective (possibly overridden) account this panel should
                // display — the real accId never changes, only which group's
                // numbers get looked up and shown for it. Only ever looked up
                // for a single-account ticket, matching allowAccountOverride
                // below and applyAccountOverrides' own gating — otherwise a
                // stale override surviving from an earlier single-account state
                // would keep silently applying with no control left to clear it.
                const effectiveAccId = isSingleAccountTicket
                  ? (accountOverrides[accId] ?? accId)
                  : accId
                // The group the backend built for effectiveAccId carries ITS
                // real exchange (server-derived from that account's own
                // trade_venue) — match on both, never accountId alone, or two
                // accounts on different exchanges that happen to share an
                // account_id could render the wrong exchange's numbers with no
                // indication anything is off (see accountIdToExchange above).
                const effectiveExchange = accountIdToExchange.get(effectiveAccId) ?? primaryExchange
                return {
                  accId,
                  effectiveAccId,
                  group: groupsByAccountAndExchange.get(`${effectiveAccId}:${effectiveExchange}`),
                }
              })
              // The footer uses account tabs directly: unlike the form rows
              // above, it is not grouped into exchange sections. Each tab owns
              // its exchange icon and account label so a cross-exchange ticket
              // stays scannable without a separate, changing exchange header.
              // `allowAccountOverride` (the panel's own "preview under a
              // different account" control) stays single-account-only,
              // matching applyAccountOverrides' own gating — a ticket spanning
              // multiple real accounts pages between THEM instead via this bar.
              const selectedAccId =
                selectedPreviewAccountId && accountIds.includes(selectedPreviewAccountId)
                  ? selectedPreviewAccountId
                  : accountIds[0]
              const tabAccounts = accountIds.flatMap((accId) => {
                const account = userAccountsData?.accounts.find((a) => a.accountId === accId)
                return account ? [account] : []
              })
              const selectedPanel = panels.find((p) => p.accId === selectedAccId)
              return (
                <div
                  data-tutorial={isLimitChaseMode ? 'chase' : undefined}
                  className="flex flex-col pt-2"
                >
                  {/* The strip class sits on this wrapper, not the tab row, so its tint
                      runs under the details chevron too. */}
                  <div className="account-tab-strip flex items-center pr-3">
                    <OverflowAccountTabs
                      accounts={tabAccounts}
                      activeAccountId={selectedAccId}
                      onSelectAccount={setSelectedPreviewAccountId}
                      className="gap-1 px-3 pt-2"
                      plainInactive
                    />
                    <Button
                      type="button"
                      variant="subtle"
                      size="icon"
                      aria-expanded={previewExpanded}
                      aria-label={previewExpanded ? t('hideDetails') : t('viewDetails')}
                      title={previewExpanded ? t('hideDetails') : t('viewDetails')}
                      onClick={() => setPreviewExpanded((v) => !v)}
                      className="mt-2 size-6 shrink-0 p-1"
                    >
                      <ChevronDownIcon
                        className={cn(
                          'size-4 transition-transform',
                          previewExpanded && 'rotate-180'
                        )}
                      />
                    </Button>
                  </div>
                  {selectedPanel && (
                    <OrderPreviewPanel
                      key={selectedPanel.accId}
                      accountId={selectedPanel.accId}
                      group={selectedPanel.group}
                      liquidation={liquidationPreviewsFor(selectedPanel.effectiveAccId)}
                      allowAccountOverride={isSingleAccountTicket}
                      previewedAccountId={selectedPanel.effectiveAccId}
                      onPreviewedAccountChange={
                        previewedAccountChangeHandlers.get(selectedPanel.accId)!
                      }
                      className="pt-0"
                      isExpanded={previewExpanded}
                    />
                  )}
                </div>
              )
            })()}
          {/* A full-width hairline between the risk summary and the submit button, so the
              numbers read as their own block rather than the button's caption. */}
          {!hideOrderPreview && !isRfqMode && !isSingleHedgeMode && (
            <div className="bg-foreground/10 h-px" />
          )}
          <div
            data-tutorial={isRfqMode ? 'execute' : isLimitChaseMode ? 'fill' : undefined}
            className={cn(
              'relative mx-auto flex w-full flex-col gap-2 px-4 py-3',
              compact && 'mx-0',
              // Frosted, so the form scrolling under it reads as passing behind the button.
              footerInScroll && 'bg-card/40 sticky bottom-0 z-10 backdrop-blur-2xl'
            )}
          >
            {isRfqMode ? (
              <SubmitButton
                isSubmitting={rfqPending}
                isFormValid={isRfqSubmittable && !venueBlocksSubmit}
                isMultiLeg={isMultiLeg}
                onSubmit={handleRfqSubmit}
                label={rfqPending ? rfqSubmitLabel : (venueIssueLabel ?? rfqSubmitLabel)}
                shortcutOwnerCardId={shortcutOwnerCardId}
                planLock={planLock}
              />
            ) : (
              <>
                {/* Floated just above the submit area rather than in flow, so an error
                    appearing doesn't grow the footer and shove the form up. */}
                {collapsibleError && (
                  <InlineError className="pointer-events-none absolute inset-x-0 bottom-full px-4 py-1">
                    {collapsibleError}
                  </InlineError>
                )}
                <SubmitButton
                  isSubmitting={isSubmitting}
                  isFormValid={isFormValid && !venueBlocksSubmit && !minNotionalBlocked}
                  isMultiLeg={isMultiLeg}
                  isTwapMode={isTwapMode}
                  isLimitChaseMode={isLimitChaseMode}
                  isMarketOrder={isMarketOrder}
                  onSubmit={() => {
                    if (priceDeviations.length > 0) {
                      setDeviationConfirmOpen(true)
                      return
                    }
                    if (guideKey) markGuideSeen(guideKey)
                    submitOrder(primarySide)
                  }}
                  shortcutOwnerCardId={shortcutOwnerCardId}
                  label={
                    isSpreaderMode
                      ? t('startSpreader')
                      : isSingleHedgeMode
                        ? hasTriggerCondition
                          ? t('createTrigger')
                          : t('hedgeDelta')
                        : isSubmitting
                          ? undefined
                          : (venueIssueLabel ?? stateSubmitLabel)
                  }
                  planLock={planLock}
                />
                <AlertDialog open={deviationConfirmOpen} onOpenChange={setDeviationConfirmOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('priceDeviationConfirmTitle')}</AlertDialogTitle>
                      <AlertDialogDescription asChild>
                        <div className="flex flex-col gap-1.5">
                          {priceDeviations.map((row) => (
                            <p key={row.key}>{priceDeviationMessage(row)}</p>
                          ))}
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          if (guideKey) markGuideSeen(guideKey)
                          submitOrder(primarySide)
                        }}
                      >
                        {t('priceDeviationConfirmAction')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </div>
      )}
    </OverlayScrollY>
  )
}

interface OrderFormSummaryProps {
  cardId?: string
  compact?: boolean
  shortcutOwnerCardId?: string
  skipProvider?: boolean
  hideCollapsibleArea?: boolean
  hideOrderPreview?: boolean
}

export default function OrderFormSummary({
  cardId,
  compact,
  shortcutOwnerCardId,
  skipProvider,
  hideCollapsibleArea,
  hideOrderPreview,
}: OrderFormSummaryProps) {
  // Use a fallback cardId if not provided (for backward compatibility)
  const effectiveCardId = cardId || 'order-form-default'

  const { selectedOptions, selectedInstruments } = useAtomValue(
    orderFormSelectionAtomFamily(effectiveCardId)
  )
  const selectedTab = useAtomValue(orderFormSummaryTabAtomFamily(effectiveCardId))
  const showAiTextfield = useAtomValue(orderFormShowAiTextfieldAtomFamily(effectiveCardId))

  // The form shows once the ticket has a row — never because another widget (a chart tab)
  // has an instrument focused. RFQ builds its own legs and the Hedge tab needs none, so both
  // show with an empty ticket.
  const hasSelectedOptions = selectedOptions.length > 0
  const hasSelectedInstruments = selectedInstruments.length > 0
  const shouldShowForm =
    selectedTab === OrderFormSummaryTab.RFQ ||
    selectedTab === OrderFormSummaryTab.SingleHedge ||
    hasSelectedOptions ||
    hasSelectedInstruments

  const content = shouldShowForm ? (
    <OrderFormContent
      compact={compact}
      shortcutOwnerCardId={shortcutOwnerCardId}
      hideCollapsibleArea={hideCollapsibleArea}
      hideOrderPreview={hideOrderPreview}
    />
  ) : (
    <OrderFormEmptyState cardId={effectiveCardId} />
  )

  // The prompt bar sits above the empty-state gate so a trade can be described into a blank ticket.
  const body = (
    <div className="flex h-full min-h-0 w-full flex-col">
      {showAiTextfield && <OrderPromptBar cardId={effectiveCardId} />}
      <div className="min-h-0 flex-1">{content}</div>
    </div>
  )

  if (skipProvider) return body

  return <OrderFormProvider cardId={effectiveCardId}>{body}</OrderFormProvider>
}
